import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { MaterialReactTable } from 'material-react-table';
import KimaruButton from './buttons/KimaruButton';
import { getMockDecisionData } from './mockDecisionData';

const CheckboxToggle = ({ checked = false, onClick = () => {}, size = 20, ariaLabel }) => {
  const borderColor = checked ? 'var(--base-primary, #0F172A)' : '#334155';
  const bg = checked ? 'var(--base-primary, #0F172A)' : 'var(--base-background, #FFF)';

  return (
    <div onClick={(e) => { e.stopPropagation(); onClick && onClick(e); }} role="button" aria-pressed={checked} aria-label={ariaLabel} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <div
        style={{
          width: size,
          height: size,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 'var(--border-radius-sm, 2px)',
          border: `1px solid ${borderColor}`,
          background: bg,
          cursor: 'pointer'
        }}
      >
        {checked && (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M13.3333 4L5.99996 11.3333L2.66663 8" stroke="#F8FAFC" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
    </div>
  );
};

const DecisionTrackerTable = ({ chartData = [] }) => {
  const userEmail = useSelector((state) => state.auth?.user?.email);
  const [rowSelection, setRowSelection] = useState({});
  const [selected, setSelected] = useState({});
  const mainKey = (id) => `main-${id}`;
  const subKey = (parentId, id) => `sub-${parentId}-${id}`;

  const toggleMain = (row) => {
    const key = mainKey(row.id);
    const isSelected = !!selected[key];
    const newSelected = { ...selected };
    if (isSelected) {
      delete newSelected[key];
      (row.decisions || []).forEach((d) => delete newSelected[subKey(row.id, d.id)]);
    } else {
      newSelected[key] = true;
      (row.decisions || []).forEach((d) => (newSelected[subKey(row.id, d.id)] = true));
    }
    setSelected(newSelected);
  };

  const toggleSub = (parentRow, subRow) => {
    const sk = subKey(parentRow.id, subRow.id);
    const mk = mainKey(parentRow.id);
    const newSelected = { ...selected };
    if (newSelected[sk]) {
      delete newSelected[sk];
    } else {
      newSelected[sk] = true;
    }

    const allSubs = (parentRow.decisions || []).map((d) => subKey(parentRow.id, d.id));
    const allSelected = allSubs.length > 0 && allSubs.every((k) => !!newSelected[k]);
    if (allSelected) newSelected[mk] = true;
    else delete newSelected[mk];

    setSelected(newSelected);
  };

  const toggleAllMain = () => {
    const allMainKeys = mainData.map((r) => mainKey(r.id));
    const allSelected = allMainKeys.every((k) => !!selected[k]);
    if (allSelected) {
      setSelected({});
    } else {
      const newSelected = {};
      mainData.forEach((r) => {
        newSelected[mainKey(r.id)] = true;
        (r.decisions || []).forEach((d) => (newSelected[subKey(r.id, d.id)] = true));
      });
      setSelected(newSelected);
    }
  };

  const mainData = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      // Get mock data based on user email
      return getMockDecisionData(userEmail);
    }

    return chartData.map((item, index) => ({
      id: index + 1,
      product: item.name || `Product ${index + 1}`,
      ...item,
      decisions: item.decisions || []
    }));
  }, [chartData, userEmail]);

  // Get month columns dynamically from data
  const monthColumns = useMemo(() => {
    if (mainData.length === 0) return [];
    
    const months = ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'];
    return months
      .filter(month => mainData[0].hasOwnProperty(month))
      .map(month => ({
        accessorKey: month,
        header: month,
        size: 100,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <span className="text-sm font-medium">
              {value ? `${value.toFixed(1)}%` : 'N/A'}
            </span>
          );
        }
      }));
  }, [mainData]);

  const mainColumns = useMemo(() => [
    {
      id: 'select',
      header: () => {
        const allMainKeys = mainData.map((r) => mainKey(r.id));
        const allSelected = allMainKeys.length > 0 && allMainKeys.every((k) => !!selected[k]);
        return (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CheckboxToggle checked={allSelected} onClick={(e) => { e.stopPropagation(); toggleAllMain(); }} ariaLabel="Select all decisions" />
          </div>
        );
      },
      size: 30,
      Cell: ({ row }) => {
        const id = row.original?.id ?? row.id;
        const key = mainKey(id);
        const isSel = !!selected[key];
        return (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CheckboxToggle checked={isSel} onClick={() => toggleMain(row.original)} ariaLabel={`Select ${row.original?.product || 'row'}`} />
          </div>
        );
      }
    },
    {
      accessorKey: 'product',
      header: 'Product',
      size: 150,
      Cell: ({ cell }) => (
        <div className="font-semi text-gray-900 text-sm">
          {cell.getValue()}
        </div>
      )
    },
    ...monthColumns,
    {
      accessorKey: 'actions',
      header: 'Actions',
      size: 120,
      Cell: () => (
        <div className="flex gap-2">
          <KimaruButton animated={true} />
        </div>
      )
    }
  ], [monthColumns]);

  const subGridColumns = [
    {
      id: 'sub-select',
      header: '',
      size: 56,
      Cell: ({ row, table }) => {
        const parent = table?.options?.dataParent || row?.original?.__parentRow || null;
        const parentId = parent?.id ?? (row?.original && row.original._parentId) ?? null;
        const key = parentId ? subKey(parentId, row.original.id) : null;
        const isSel = key ? !!selected[key] : false;
        return (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CheckboxToggle checked={isSel} onClick={(e) => { e.stopPropagation(); if (parentId) toggleSub({ id: parentId, decisions: parent?.decisions || [] }, row.original); }} ariaLabel={`Select ${row.original?.product || 'decision'}`} />
          </div>
        );
      }
    },
    {
      accessorKey: 'date',
      header: 'Date',
      size: 120,
      Cell: ({ cell }) => <span className="text-sm">{cell.getValue()}</span>
    },
    {
      accessorKey: 'decision',
      header: 'Decision',
      size: 150,
      Cell: ({ cell }) => (
        <span className="text-sm font-medium">{cell.getValue()}</span>
      )
    },
    {
      accessorKey: 'product',
      header: 'Product',
      size: 180,
      Cell: ({ cell }) => <span className="text-sm">{cell.getValue()}</span>
    },
    {
      accessorKey: 'disruptionReason',
      header: 'Disruption Reason',
      size: 150,
      Cell: ({ cell }) => (
        <span className="text-sm">{cell.getValue()}</span>
      )
    },
    {
      accessorKey: 'decisionBy',
      header: 'Decision by',
      size: 120,
      Cell: ({ cell }) => (
        <span className="text-sm font-medium text-gray-700">
          {cell.getValue()}
        </span>
      )
    },
    {
      accessorKey: 'predictedOutcome',
      header: 'Predicted Outcome',
      size: 160,
      Cell: ({ cell }) => (
        <span className="text-sm text-gray-600">{cell.getValue()}</span>
      )
    },
    {
      accessorKey: 'actualOutcome',
      header: 'Actual Outcome',
      size: 180,
      Cell: ({ cell }) => (
        <span className="text-sm text-gray-600">{cell.getValue()}</span>
      )
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      size: 120,
      Cell: () => (
        <div className="flex gap-2">
          <KimaruButton animated={true} />
        </div>
      )
    }
  ];

  return (
    <div className="mx-auto">
      <div className="bg-white rounded-xl shadow-sm border border-slate-100">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <p style={{ fontSize: "18px" }}> Select to ask questions about multiple decisions <KimaruButton disabled={true} className="m1-4" style={{ opacity: 0.6}} /></p>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                style={{
                  borderRadius: "var(--border-radius-md, 6px)",
                  border: "1px solid var(--base-input, #E2E8F0)",
                  background: "var(--base-background, #FFF)",
                  display: "flex",
                  height: "var(--width-w-9, 36px)",
                  padding: "var(--spacing-2, 8px) var(--spacing-4, 16px)",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "var(--spacing-2, 8px)"
                }}
              >
                Filter
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M2 4H14M4.66667 8H11.3333M6.66667 12H9.33333" stroke="#0F172A" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              <button
                style={{
                  borderRadius: "var(--border-radius-md, 6px)",
                  border: "1px solid var(--base-input, #E2E8F0)",
                  background: "var(--base-background, #FFF)",
                  display: "flex",
                  height: "var(--width-w-9, 36px)",
                  padding: "var(--spacing-2, 8px) var(--spacing-4, 16px)",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "var(--spacing-2, 8px)"
                }}
              >
                Export
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="16" viewBox="0 0 12 16" fill="none">
                  <path d="M7.33341 1.3335V4.00016C7.33341 4.35378 7.47389 4.69292 7.72394 4.94297C7.97399 5.19302 8.31313 5.3335 8.66675 5.3335H11.3334M8.00008 1.3335H2.00008C1.64646 1.3335 1.30732 1.47397 1.05727 1.72402C0.807224 1.97407 0.666748 2.31321 0.666748 2.66683V13.3335C0.666748 13.6871 0.807224 14.0263 1.05727 14.2763C1.30732 14.5264 1.64646 14.6668 2.00008 14.6668H10.0001C10.3537 14.6668 10.6928 14.5264 10.9429 14.2763C11.1929 14.0263 11.3334 13.6871 11.3334 13.3335V4.66683L8.00008 1.3335Z" stroke="#0F172A" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
          <MaterialReactTable
            columns={mainColumns}
            data={mainData}
            enableExpanding={true}
            getCanExpand={() => true}
            muiExpandButtonProps={({ row }) => ({
              sx: {
                transform: row.getIsExpanded() ? 'rotate(180deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s ease-in-out',
                '& .MuiIconButton-root': {
                  padding: '0px',
                }
              }
            })}
            muiTableBodyRowProps={() => ({
              sx: {
                cursor: 'pointer',
                backgroundColor: '#ffffff',
                '&:hover': {
                  backgroundColor: '#f8f9fa',
                }
              },
            })}
            muiTableProps={{
              sx: {
                '& .MuiTableRow-root': {
                  borderSpacing: '0',
                },
              }
            }}
            muiTableContainerProps={{
              sx: {
                '& .MuiTable-root': {
                  borderCollapse: 'collapse',
                  borderSpacing: '0',
                }
              }
            }}
            state={{ rowSelection }}
            onRowSelectionChange={setRowSelection}
            enableHiding={false}
            enableDensityToggle={false}
            enableTopToolBar={false}
            enableColumnActions={false}
            enableSorting={false}
            enableGlobalFilter={false}
            enableColumnFilters={false}
            enableFullScreenToggle={false}
            muiTableHeadProps={{
              sx: {
                'th .Mui-TableHeadCell-Content-Labels, th .Mui-TableHeadCell-Content-Wrapper': {
                  width: '100%',
                },
                'th:last-child': {
                  borderRight: '0',
                },
                th: {
                  backgroundColor: '#f8fafc',
                  padding: '12px 16px',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  color: '#0a0a0aff',
                  borderBottom: '1px solid #e2e8f0',
                },
              },
            }}
            muiTableBodyCellProps={{
              sx: {
                padding: '12px 16px',
                borderBottom: '1px solid #e2e8f0',
                verticalAlign: 'middle',
                backgroundColor: '#ffffff',
              }
            }}
            muiTablePaperProps={{
              sx: {
                boxShadow: 'none',
                borderRadius: '0',
                overflow: 'hidden',
                backgroundColor: '#ffffff',
                border: 'none',
              }
            }}
            renderDetailPanel={({ row }) => {
              const product = row.original;
              const decisionsWithParent = (product.decisions || []).map((d) => ({ ...d, _parentId: product.id }));
              return (
                <div className="p-0 ml-8" onClick={(e) => e.stopPropagation()}>
                  <MaterialReactTable
                    columns={subGridColumns}
                    data={decisionsWithParent}
                    options={{ dataParent: product }}
                    muiTablePaperProps={{
                      elevation: 0,
                      sx: {
                        margin: '0',
                        boxShadow: 'none',
                        backgroundColor: '#ffffff',
                      }
                    }}
                    muiTableContainerProps={{
                      sx: {
                        '& .MuiTable-root': {
                          borderCollapse: 'collapse',
                          borderSpacing: '0',
                          marginTop: '0',
                          marginBottom: '0',
                        }
                      }
                    }}
                    muiTableBodyRowProps={() => ({
                      sx: {
                        cursor: 'pointer',
                        backgroundColor: '#ffffff',
                        '&:hover': {
                          backgroundColor: '#f9f9f9',
                        }
                      },
                    })}
                    enableBottomToolbar={false}
                    enableTopToolbar={false}
                    enableColumnActions={false}
                    enableRowSelection={false}
                    enableSelectAll={false}
                    enableSorting={false}
                    enableGlobalFilter={false}
                    enableColumnFilters={false}
                    muiTableHeadProps={{
                      sx: {
                        th: {
                          backgroundColor: '#f8fafc',
                          borderBottom: '1px solid #e2e8f0',
                          padding: '8px 16px',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          color: '#64748b',
                        },
                      },
                    }}
                    muiTableBodyCellProps={{
                      sx: {
                        padding: '8px 16px',
                        borderBottom: '1px solid #e2e8f0',
                        verticalAlign: 'middle',
                        backgroundColor: '#ffffff',
                      }
                    }}
                  />
                </div>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default DecisionTrackerTable;